import { Space, Tag, Tooltip, Typography } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { shortId } from '@/shared/lib/shortId'
import { fetchWarehouses } from './stockApi'

interface WarehouseRefProps {
  nodeId: string | null
}

/**
 * Omborga havola — id o'rniga NOM (`SiteRef` bilan bir xil naqsh).
 *
 * ⚠️ BO'SH QAYTARMAYDI. Beshta holat ALOHIDA chiziladi, chunki ular
 * bir-biridan boshqa ishni talab qiladi:
 *
 *   · `nodeId === null`  → "ko'rsatilmagan". ⚠️ Bu ma'lumot sifatining
 *     belgisi EMAS, xatoning belgisi: omborsiz jihoz `SUBTREE` doirali
 *     foydalanuvchiga umuman ko'rinmaydi (`V202608131400` §3.1);
 *   · huquq yo'q         → sabab yoziladi, "ombor yo'q" DEYILMAYDI;
 *   · yuklanmoqda        → hali xulosa chiqarilmaydi;
 *   · so'rov yiqildi     → id ko'rsatiladi, lekin "noma'lum" deb
 *     AYBLANMAYDI — ma'lumot to'g'ri bo'lishi mumkin;
 *   · javob keldi, topilmadi → SARIQ ogohlantirish (doiradan tashqarida
 *     yoki turidan `holds_stock` olib tashlangan).
 */
export function WarehouseRef({ nodeId }: WarehouseRefProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const allowed = can('inventory.stock.read')

  const query = useQuery({
    queryKey: queryKeys.stock.warehouses,
    queryFn: fetchWarehouses,
    enabled: allowed && nodeId !== null,
    staleTime: 5 * 60_000,
  })

  if (nodeId === null) {
    return (
      <Tooltip title={t('inventory.warehouseUnsetHint')}>
        <Typography.Text type="secondary" italic>
          {t('inventory.warehouseUnset')}
        </Typography.Text>
      </Tooltip>
    )
  }

  if (!allowed) {
    return (
      <Tooltip title={t('inventory.warehouseNoPermission')}>
        <Typography.Text type="secondary" code>
          {/* Qisqartirish YAGONA joyda — `shared/lib/shortId.ts` (B12) */}
          {shortId(nodeId)}
        </Typography.Text>
      </Tooltip>
    )
  }

  const found = query.data?.find((warehouse) => warehouse.id === nodeId)
  if (found) {
    return (
      <Space size={4} wrap>
        <Typography.Text>{found.displayName}</Typography.Text>
        <Typography.Text type="secondary">{found.code}</Typography.Text>
        {found.state === 'CLOSED' ? <Tag color="default">{t('orgNodeState.CLOSED')}</Tag> : null}
      </Space>
    )
  }

  if (query.isPending) {
    return <Typography.Text type="secondary">{t('common.loading')}</Typography.Text>
  }

  // ⚠️ "Topilmadi" xulosasi FAQAT javob kelganda. Yiqilgan so'rovda ro'yxat
  // ham bo'sh bo'ladi va sariq ogohlantirish YOLG'ON bo'lardi
  // (`guards.mjs` §3 dagi o'sha nuqson).
  if (!query.isSuccess) {
    return (
      <Tooltip title={t('inventory.warehouseUnavailable')}>
        <Typography.Text type="secondary" code>
          {/* Qisqartirish YAGONA joyda — `shared/lib/shortId.ts` (B12) */}
          {shortId(nodeId)}
        </Typography.Text>
      </Tooltip>
    )
  }

  return (
    <Tooltip title={t('inventory.warehouseUnknownHint')}>
      <Tag icon={<WarningOutlined />} color="warning">
        {t('inventory.warehouseUnknown')}
      </Tag>
    </Tooltip>
  )
}
